import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Gift, Sparkles, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ses efektleri için AudioContext
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

const SpinWheel = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [canSpin, setCanSpin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const wheelRef = useRef(null);
  const audioContextRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    fetchRewards();
    checkSpinStatus();
    return () => {
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gamification/spin-rewards`);
      setRewards(response.data.rewards);
    } catch (err) {
      console.error('Error fetching rewards:', err);
    }
  };

  const checkSpinStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gamification/profile`, { withCredentials: true });
      setCanSpin(response.data.can_spin);
      setIsAdmin(response.data.is_admin || false);
    } catch (err) {
      console.error('Error checking spin status:', err);
    }
  };

  // Tick sesi
  const playTickSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = createAudioContext();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = 800 + Math.random() * 400;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [soundEnabled]);

  // Kazanma sesi
  const playWinSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = createAudioContext();
      }
      const ctx = audioContextRef.current;
      
      // Fanfare sesi
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        const startTime = ctx.currentTime + i * 0.15;
        gainNode.gain.setValueAtTime(0.2, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.3);
      });
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [soundEnabled]);

  const spinWheel = async () => {
    if (!canSpin || spinning) return;

    setSpinning(true);
    setResult(null);
    setShowConfetti(false);

    try {
      const response = await axios.post(`${API_URL}/api/gamification/spin`, {}, { withCredentials: true });
      
      // Ödülün index'ini bul
      const rewardIndex = rewards.findIndex(r => r.id === response.data.reward.id);
      
      // Çarkı döndür - daha fazla tur ve daha gerçekçi fizik
      const segmentAngle = 360 / rewards.length;
      const targetAngle = (rewardIndex * segmentAngle) + (segmentAngle / 2);
      const spins = 8 + Math.random() * 4; // 8-12 tur
      const totalRotation = spins * 360 + (360 - targetAngle);
      
      // Animasyon süresi
      const duration = 6000 + Math.random() * 2000; // 6-8 saniye
      const startTime = Date.now();
      const startRotation = rotation;
      
      // Tick sesi için interval
      let lastTickAngle = startRotation;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function - yavaşça durma efekti
        const easeOut = 1 - Math.pow(1 - progress, 4);
        
        const currentRotation = startRotation + (totalRotation * easeOut);
        setRotation(currentRotation);
        
        // Hız hesapla (görsel gösterge için)
        const speed = (totalRotation * (1 - progress) * 4 * Math.pow(1 - progress, 3)) / 360;
        setCurrentSpeed(Math.max(0, speed));
        
        // Tick sesi - her segment geçişinde
        const currentAngle = currentRotation % 360;
        const angleDiff = Math.abs(currentAngle - (lastTickAngle % 360));
        if (angleDiff > segmentAngle * 0.8 || angleDiff < -segmentAngle * 0.8) {
          playTickSound();
          lastTickAngle = currentRotation;
        }
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Animasyon bitti
          setSpinning(false);
          // Admin için her zaman çevirebilir, normal kullanıcılar için kapat
          if (!isAdmin) {
            setCanSpin(false);
          }
          setResult(response.data.reward);
          setShowConfetti(true);
          setCurrentSpeed(0);
          playWinSound();
          toast.success(response.data.message);
          
          // Confetti'yi 5 saniye sonra kapat
          setTimeout(() => setShowConfetti(false), 5000);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
    } catch (err) {
      console.error('Spin error:', err);
      toast.error(err.response?.data?.detail || 'Çark çevrilemedi');
      setSpinning(false);
    }
  };

  // Confetti komponenti
  const Confetti = () => (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-20px',
            width: `${8 + Math.random() * 8}px`,
            height: `${8 + Math.random() * 8}px`,
            backgroundColor: ['#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'][Math.floor(Math.random() * 6)],
            borderRadius: Math.random() > 0.5 ? '50%' : '0',
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${3 + Math.random() * 2}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900">
      {/* Confetti */}
      {showConfetti && <Confetti />}
      
      {/* Arka plan ışık efektleri */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-purple-500/20 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-orange-500/20 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-4 pb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            {soundEnabled ? (
              <Volume2 className="w-5 h-5 text-white" />
            ) : (
              <VolumeX className="w-5 h-5 text-white/50" />
            )}
          </button>
        </div>
        
        <div className="text-center mt-6">
          <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400">
            🎰 ŞANS ÇARKI
          </h1>
          <p className="text-amber-200/70 text-sm mt-2">Her gün bir kez çevir, muhteşem ödüller kazan!</p>
        </div>
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-4 py-4">
        {/* Ana Çark Alanı */}
        <div className="relative">
          {/* Dış ışıklı çerçeve */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`w-80 h-80 rounded-full ${spinning ? 'animate-pulse' : ''}`}
              style={{
                background: 'conic-gradient(from 0deg, #f59e0b, #ef4444, #ec4899, #8b5cf6, #3b82f6, #10b981, #f59e0b)',
                filter: 'blur(15px)',
                opacity: spinning ? 0.8 : 0.4
              }}
            />
          </div>

          {/* Çark Container */}
          <div className="relative w-72 h-72 mx-auto">
            {/* Dış halka - LED ışıkları */}
            <div className="absolute inset-[-20px] rounded-full border-8 border-gray-800 bg-gray-900 shadow-2xl overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                {[...Array(24)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute w-3 h-3 rounded-full transition-all duration-100 ${
                      spinning ? 'animate-pulse' : ''
                    }`}
                    style={{
                      backgroundColor: spinning ? ['#f59e0b', '#ef4444', '#10b981', '#3b82f6'][i % 4] : '#374151',
                      boxShadow: spinning ? `0 0 10px ${['#f59e0b', '#ef4444', '#10b981', '#3b82f6'][i % 4]}` : 'none',
                      top: `${50 + 47 * Math.sin((i * 15 * Math.PI) / 180)}%`,
                      left: `${50 + 47 * Math.cos((i * 15 * Math.PI) / 180)}%`,
                      transform: 'translate(-50%, -50%)',
                      animationDelay: `${i * 50}ms`
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Çark SVG */}
            <svg
              ref={wheelRef}
              viewBox="0 0 300 300"
              className="w-full h-full relative z-10 drop-shadow-2xl"
              style={{ 
                transform: `rotate(${rotation}deg)`,
                filter: 'drop-shadow(0 0 20px rgba(0,0,0,0.5))'
              }}
            >
              {/* Dış halka gölgesi */}
              <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#000" floodOpacity="0.5"/>
                </filter>
                <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#fcd34d" />
                  <stop offset="50%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#d97706" />
                </linearGradient>
              </defs>
              
              {/* Çark dilimleri */}
              {rewards.map((reward, index) => {
                const angle = (360 / rewards.length) * index;
                const startAngle = angle - 90;
                const endAngle = startAngle + (360 / rewards.length);
                
                const startRad = (startAngle * Math.PI) / 180;
                const endRad = (endAngle * Math.PI) / 180;
                
                const x1 = 150 + 140 * Math.cos(startRad);
                const y1 = 150 + 140 * Math.sin(startRad);
                const x2 = 150 + 140 * Math.cos(endRad);
                const y2 = 150 + 140 * Math.sin(endRad);
                
                const largeArc = (360 / rewards.length) > 180 ? 1 : 0;
                
                const midAngle = (startAngle + endAngle) / 2;
                const midRad = (midAngle * Math.PI) / 180;
                const textX = 150 + 95 * Math.cos(midRad);
                const textY = 150 + 95 * Math.sin(midRad);
                
                // 3D efekt için alternatif renkler
                const darkerColor = reward.color;
                const lighterColor = reward.color + 'dd';
                
                return (
                  <g key={reward.id}>
                    {/* Ana dilim */}
                    <path
                      d={`M 150 150 L ${x1} ${y1} A 140 140 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={index % 2 === 0 ? darkerColor : lighterColor}
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="2"
                      filter="url(#shadow)"
                    />
                    {/* İç highlight */}
                    <path
                      d={`M 150 150 L ${150 + 130 * Math.cos(startRad)} ${150 + 130 * Math.sin(startRad)} A 130 130 0 ${largeArc} 1 ${150 + 130 * Math.cos(endRad)} ${150 + 130 * Math.sin(endRad)} Z`}
                      fill="rgba(255,255,255,0.1)"
                    />
                    {/* Metin */}
                    <text
                      x={textX}
                      y={textY}
                      fill="white"
                      fontSize="11"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${midAngle + 90}, ${textX}, ${textY})`}
                      style={{ 
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        fontFamily: 'system-ui'
                      }}
                    >
                      {reward.name}
                    </text>
                  </g>
                );
              })}
              
              {/* Merkez düğme - 3D efekt */}
              <circle cx="150" cy="150" r="35" fill="url(#goldGradient)" stroke="#854d0e" strokeWidth="3" />
              <circle cx="150" cy="150" r="28" fill="#fef3c7" />
              <circle cx="150" cy="150" r="22" fill="url(#goldGradient)" />
              <text x="150" y="156" fill="#78350f" fontSize="24" textAnchor="middle" fontWeight="bold">🎰</text>
            </svg>
            
            {/* Ok işareti - 3D */}
            <div className="absolute top-[-15px] left-1/2 -translate-x-1/2 z-20">
              <div className="relative">
                <div className="w-0 h-0 border-l-[18px] border-r-[18px] border-t-[35px] border-l-transparent border-r-transparent border-t-amber-400"
                  style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.5))' }}
                />
                <div className="absolute top-[2px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[28px] border-l-transparent border-r-transparent border-t-yellow-300" />
              </div>
            </div>

            {/* Hız göstergesi */}
            {spinning && currentSpeed > 0.1 && (
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-amber-400 text-sm font-bold animate-pulse">
                ⚡ {Math.round(currentSpeed * 100)} km/s
              </div>
            )}
          </div>
        </div>

        {/* Spin Butonu */}
        <div className="mt-8">
          <Button
            onClick={spinWheel}
            disabled={!canSpin || spinning}
            className={`w-full py-6 text-xl font-black rounded-2xl transition-all duration-300 transform ${
              canSpin && !spinning
                ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-400 hover:via-orange-400 hover:to-red-400 hover:scale-105 shadow-lg shadow-orange-500/50'
                : 'bg-gray-700 text-gray-400'
            }`}
          >
            {spinning ? (
              <span className="flex items-center justify-center gap-3">
                <Sparkles className="w-6 h-6 animate-spin" />
                ÇARK DÖNÜYOR...
              </span>
            ) : canSpin ? (
              <span className="flex items-center justify-center gap-3">
                <Gift className="w-6 h-6" />
                ÇARKI ÇEVİR!
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                ⏰ YARIN TEKRAR GEL!
              </span>
            )}
          </Button>
        </div>

        {/* Sonuç */}
        {result && (
          <Card className="mt-6 border-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 animate-pulse" />
            <CardContent className="p-6 text-center relative">
              <div className="text-5xl mb-2 animate-bounce">🎉</div>
              <p className="text-amber-300 font-bold text-xl">TEBRİKLER!</p>
              <p className="text-3xl font-black text-white mt-2 animate-pulse">{result.name}</p>
              <p className="text-amber-200/80 text-sm mt-3">
                {result.type === 'miles' && '✨ Mil hesabınıza eklendi!'}
                {result.type === 'multiplier' && '🔥 Bugün tüm puanlar 2 KATI!'}
                {result.type === 'shield' && '🛡️ Streak kalkanınız aktif!'}
                {result.type === 'task' && '⭐ Bonus görev açıldı!'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Ödül Listesi */}
        <Card className="border-0 bg-white/5 backdrop-blur-sm mt-6">
          <CardContent className="p-4">
            <h3 className="font-bold text-amber-300 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Olası Ödüller
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {rewards.map(reward => (
                <div
                  key={reward.id}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div
                    className="w-3 h-3 rounded-full shadow-lg"
                    style={{ 
                      backgroundColor: reward.color,
                      boxShadow: `0 0 8px ${reward.color}`
                    }}
                  />
                  <span className="text-sm font-medium text-white/80">{reward.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SpinWheel;
